// src/lib/cabinetI18n.js
// Textes du Cabinet de curiosités en français, anglais et allemand.
// Dictionnaire local (pas de dépendance aux gros fichiers de traduction) ; langue = celle de l'interface.
import { useTranslation } from "react-i18next";

const LOCALES = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

const D = {
  fr: {
    title: "Cabinet de curiosités",
    loading: "Chargement…",
    notOpen: "Cette fonctionnalité n'est pas encore ouverte pour votre compte.",
    backToCollection: "← Retour à la collection",
    ownerOnly: "Seul le propriétaire de la collection peut composer un cabinet.",
    collectionFallback: "Collection",
    defaultTitle: (c) => (c ? `Cabinet — ${c}` : "Mon cabinet"),
    intro: "Composez un accrochage à partir des œuvres de cette collection : choisissez, ordonnez, racontez.",
    titleLabel: "Titre du cabinet",
    create: "Créer mon cabinet",
    background: "Fond :",
    unsaved: "Modifications non enregistrées",
    version: (n) => `Version ${n}`,
    exportPng: "Exporter en image",
    save: "Enregistrer",
    collectionWorks: (n) => `Œuvres de la collection (${n})`,
    collectionWorksAria: "Œuvres de la collection",
    alreadyHung: "Déjà accrochée",
    hang: "Accrocher",
    untitled: "Sans titre",
    hangingAria: "Accrochage",
    emptyWall: "Cliquez sur une œuvre pour l'accrocher au mur.",
    noImage: "Sans image",
    removedWork: "Œuvre retirée",
    captionPlaceholder: "Un mot sur cette œuvre…",
    captionFor: (t) => `Texte pour ${t}`,
    theWork: "l'œuvre",
    back: "Reculer",
    forward: "Avancer",
    otherView: "Autre vue",
    remove: "Retirer",
    reload: "Recharger la dernière version",
    saved: (n) => `Version ${n} enregistrée.`,
    restoreConfirm: "Vos changements non enregistrés seront remplacés. Continuer ?",
    restored: (n) => `Version ${n} rechargée : enregistrez pour la rétablir comme nouvelle version.`,
    saveFirst: "Enregistrez d'abord vos modifications : on partage une version enregistrée.",
    inviteDone: (email, link) => `Invitation créée pour ${email}. Envoyez-lui ce lien (il ne fonctionne qu'après connexion avec cette adresse) : ${link}`,
    revokeConfirm: "Révoquer cet accès ? La personne ne pourra plus rouvrir le cabinet (une capture d'écran déjà faite reste hors de notre portée).",
    revoked: "Accès révoqué.",
    exported: "Image exportée.",
    exportedPartial: (n) => `Image exportée, mais ${n} œuvre(s) n'ont pas pu être dessinées (image illisible).`,
    exportFailed: "Export impossible : une image n'est pas lisible depuis le navigateur.",
    shareSummary: (n) => `Partager avec un proche (${n})`,
    shareHelp: (v) => `La personne reçoit un accès à la version ${v} (figée), en lecture seule, avec le titre, l'artiste, l'année et vos textes. Elle doit se connecter avec l'adresse e-mail indiquée.`,
    emailPlaceholder: "adresse e-mail",
    emailAria: "Adresse e-mail du destinataire",
    durationAria: "Durée de l'accès",
    days: (n) => (n === 365 ? "1 an" : `${n} jours`),
    invite: "Inviter",
    statusRevoked: "révoqué",
    statusExpired: "expiré",
    statusUntil: (d) => `jusqu'au ${d}`,
    views: (n) => `${n} consultation${n > 1 ? "s" : ""}`,
    revoke: "Révoquer",
    previousVersions: (n) => `Versions précédentes (${n})`,
    versionLine: (n, d) => `Version ${n} · ${d}`,
    restore: "Rétablir",
    // thèmes
    theme_ardoise: "Ardoise", theme_bordeaux: "Bordeaux", theme_vert: "Vert anglais", theme_lin: "Lin",
    // erreurs
    e_conflict: "Ce cabinet a été modifié ailleurs depuis votre dernier chargement.",
    e_artwork: "Une des œuvres n'appartient pas à cette collection.",
    e_disabled: "Le Cabinet de curiosités n'est pas encore ouvert pour votre compte.",
    e_notOwned: "Seul le propriétaire de la collection peut créer un cabinet.",
    e_titleRequired: "Donnez un titre à votre cabinet.",
    e_generic: "Enregistrement impossible pour le moment.",
    e_self: "Vous ne pouvez pas vous inviter vous-même.",
    e_email: "Cette adresse e-mail n'est pas valide.",
    e_already: "Cette version est déjà partagée avec cette personne.",
    e_tooMany: "Vous avez atteint le nombre maximal de partages actifs.",
    e_duration: "Durée de partage invalide.",
    // carte du tableau de bord
    cardEyebrow: "Nouveau",
    cardBody: "Composez un accrochage à partir d'une collection : choisissez, ordonnez, racontez, puis partagez avec un proche.",
    cardCreate: (t) => `Créer un cabinet : ${t}`,
    cardNoCollection: "Créez d'abord une collection pour y composer un cabinet.",
    cardShared: (n) => `${n} cabinet${n > 1 ? "s" : ""} partagé${n > 1 ? "s" : ""} avec moi`,
    // page des cabinets reçus
    sharedTitle: "Cabinets partagés avec moi",
    sharedLoadError: "Impossible de charger vos invitations pour le moment.",
    sharedNone: "Aucune invitation active pour l'adresse avec laquelle vous êtes connecté.",
    sharedFrom: (n) => ` · de ${n}`,
    sharedUntil: (d) => ` · jusqu'au ${d}`,
    unavailableTitle: "Cabinet indisponible",
    unavailableBody: "Cet accès n'existe pas, a expiré ou a été révoqué — ou vous n'êtes pas connecté avec l'adresse e-mail invitée.",
    myInvitations: "Mes invitations",
    sharedReadOnly: (d) => `Partagé avec vous, en lecture seule · accès jusqu'au ${d}`,
    sidebarLink: "Cabinet de curiosités",
  },
  en: {
    title: "Cabinet of curiosities",
    loading: "Loading…",
    notOpen: "This feature is not yet open for your account.",
    backToCollection: "← Back to the collection",
    ownerOnly: "Only the owner of the collection can compose a cabinet.",
    collectionFallback: "Collection",
    defaultTitle: (c) => (c ? `Cabinet — ${c}` : "My cabinet"),
    intro: "Compose a hanging from the works of this collection: choose, arrange, tell their story.",
    titleLabel: "Cabinet title",
    create: "Create my cabinet",
    background: "Background:",
    unsaved: "Unsaved changes",
    version: (n) => `Version ${n}`,
    exportPng: "Export as image",
    save: "Save",
    collectionWorks: (n) => `Works in the collection (${n})`,
    collectionWorksAria: "Works in the collection",
    alreadyHung: "Already hung",
    hang: "Hang",
    untitled: "Untitled",
    hangingAria: "Hanging",
    emptyWall: "Click a work to hang it on the wall.",
    noImage: "No image",
    removedWork: "Work removed",
    captionPlaceholder: "A word about this work…",
    captionFor: (t) => `Text for ${t}`,
    theWork: "the work",
    back: "Move back",
    forward: "Move forward",
    otherView: "Other view",
    remove: "Remove",
    reload: "Reload the latest version",
    saved: (n) => `Version ${n} saved.`,
    restoreConfirm: "Your unsaved changes will be replaced. Continue?",
    restored: (n) => `Version ${n} reloaded: save to restore it as a new version.`,
    saveFirst: "Save your changes first: a saved version is what gets shared.",
    inviteDone: (email, link) => `Invitation created for ${email}. Send them this link (it only works after signing in with that address): ${link}`,
    revokeConfirm: "Revoke this access? The person will no longer be able to reopen the cabinet (a screenshot already taken is beyond our reach).",
    revoked: "Access revoked.",
    exported: "Image exported.",
    exportedPartial: (n) => `Image exported, but ${n} work(s) could not be drawn (unreadable image).`,
    exportFailed: "Export failed: an image cannot be read from the browser.",
    shareSummary: (n) => `Share with someone close (${n})`,
    shareHelp: (v) => `The person gets read-only access to version ${v} (frozen), with the title, artist, year and your texts. They must sign in with the e-mail address given.`,
    emailPlaceholder: "e-mail address",
    emailAria: "Recipient's e-mail address",
    durationAria: "Access duration",
    days: (n) => (n === 365 ? "1 year" : `${n} days`),
    invite: "Invite",
    statusRevoked: "revoked",
    statusExpired: "expired",
    statusUntil: (d) => `until ${d}`,
    views: (n) => `${n} view${n > 1 ? "s" : ""}`,
    revoke: "Revoke",
    previousVersions: (n) => `Previous versions (${n})`,
    versionLine: (n, d) => `Version ${n} · ${d}`,
    restore: "Restore",
    theme_ardoise: "Slate", theme_bordeaux: "Burgundy", theme_vert: "British green", theme_lin: "Linen",
    e_conflict: "This cabinet was changed elsewhere since you last loaded it.",
    e_artwork: "One of the works does not belong to this collection.",
    e_disabled: "The Cabinet of curiosities is not yet open for your account.",
    e_notOwned: "Only the owner of the collection can create a cabinet.",
    e_titleRequired: "Give your cabinet a title.",
    e_generic: "Could not save right now.",
    e_self: "You cannot invite yourself.",
    e_email: "This e-mail address is not valid.",
    e_already: "This version is already shared with this person.",
    e_tooMany: "You have reached the maximum number of active shares.",
    e_duration: "Invalid sharing duration.",
    cardEyebrow: "New",
    cardBody: "Compose a hanging from a collection: choose, arrange, tell the story, then share it with someone close.",
    cardCreate: (t) => `Create a cabinet: ${t}`,
    cardNoCollection: "Create a collection first to compose a cabinet in it.",
    cardShared: (n) => `${n} cabinet${n > 1 ? "s" : ""} shared with me`,
    sharedTitle: "Cabinets shared with me",
    sharedLoadError: "Could not load your invitations right now.",
    sharedNone: "No active invitation for the address you are signed in with.",
    sharedFrom: (n) => ` · from ${n}`,
    sharedUntil: (d) => ` · until ${d}`,
    unavailableTitle: "Cabinet unavailable",
    unavailableBody: "This access does not exist, has expired or was revoked — or you are not signed in with the invited e-mail address.",
    myInvitations: "My invitations",
    sharedReadOnly: (d) => `Shared with you, read only · access until ${d}`,
    sidebarLink: "Cabinet of curiosities",
  },
  de: {
    title: "Kuriositätenkabinett",
    loading: "Wird geladen…",
    notOpen: "Diese Funktion ist für Ihr Konto noch nicht freigeschaltet.",
    backToCollection: "← Zurück zur Sammlung",
    ownerOnly: "Nur der Eigentümer der Sammlung kann ein Kabinett zusammenstellen.",
    collectionFallback: "Sammlung",
    defaultTitle: (c) => (c ? `Kabinett — ${c}` : "Mein Kabinett"),
    intro: "Stellen Sie aus den Werken dieser Sammlung eine Hängung zusammen: auswählen, ordnen, erzählen.",
    titleLabel: "Titel des Kabinetts",
    create: "Mein Kabinett anlegen",
    background: "Hintergrund:",
    unsaved: "Nicht gespeicherte Änderungen",
    version: (n) => `Version ${n}`,
    exportPng: "Als Bild exportieren",
    save: "Speichern",
    collectionWorks: (n) => `Werke der Sammlung (${n})`,
    collectionWorksAria: "Werke der Sammlung",
    alreadyHung: "Bereits gehängt",
    hang: "Aufhängen",
    untitled: "Ohne Titel",
    hangingAria: "Hängung",
    emptyWall: "Klicken Sie auf ein Werk, um es an die Wand zu hängen.",
    noImage: "Kein Bild",
    removedWork: "Werk entfernt",
    captionPlaceholder: "Ein Wort zu diesem Werk…",
    captionFor: (t) => `Text zu ${t}`,
    theWork: "dem Werk",
    back: "Nach vorn verschieben",
    forward: "Nach hinten verschieben",
    otherView: "Andere Ansicht",
    remove: "Entfernen",
    reload: "Neueste Version laden",
    saved: (n) => `Version ${n} gespeichert.`,
    restoreConfirm: "Ihre nicht gespeicherten Änderungen werden ersetzt. Fortfahren?",
    restored: (n) => `Version ${n} geladen: Speichern Sie, um sie als neue Version wiederherzustellen.`,
    saveFirst: "Speichern Sie zuerst Ihre Änderungen: Geteilt wird eine gespeicherte Version.",
    inviteDone: (email, link) => `Einladung für ${email} erstellt. Senden Sie diese Person diesen Link (er funktioniert nur nach Anmeldung mit dieser Adresse): ${link}`,
    revokeConfirm: "Diesen Zugang widerrufen? Die Person kann das Kabinett nicht mehr öffnen (ein bereits gemachter Screenshot liegt außerhalb unserer Reichweite).",
    revoked: "Zugang widerrufen.",
    exported: "Bild exportiert.",
    exportedPartial: (n) => `Bild exportiert, aber ${n} Werk(e) konnten nicht gezeichnet werden (Bild nicht lesbar).`,
    exportFailed: "Export nicht möglich: Ein Bild ist im Browser nicht lesbar.",
    shareSummary: (n) => `Mit einer nahestehenden Person teilen (${n})`,
    shareHelp: (v) => `Die Person erhält schreibgeschützten Zugang zu Version ${v} (eingefroren) mit Titel, Künstler, Jahr und Ihren Texten. Sie muss sich mit der angegebenen E-Mail-Adresse anmelden.`,
    emailPlaceholder: "E-Mail-Adresse",
    emailAria: "E-Mail-Adresse des Empfängers",
    durationAria: "Dauer des Zugangs",
    days: (n) => (n === 365 ? "1 Jahr" : `${n} Tage`),
    invite: "Einladen",
    statusRevoked: "widerrufen",
    statusExpired: "abgelaufen",
    statusUntil: (d) => `bis ${d}`,
    views: (n) => `${n} Aufruf${n > 1 ? "e" : ""}`,
    revoke: "Widerrufen",
    previousVersions: (n) => `Frühere Versionen (${n})`,
    versionLine: (n, d) => `Version ${n} · ${d}`,
    restore: "Wiederherstellen",
    theme_ardoise: "Schiefer", theme_bordeaux: "Bordeaux", theme_vert: "Englischgrün", theme_lin: "Leinen",
    e_conflict: "Dieses Kabinett wurde seit Ihrem letzten Laden anderswo geändert.",
    e_artwork: "Eines der Werke gehört nicht zu dieser Sammlung.",
    e_disabled: "Das Kuriositätenkabinett ist für Ihr Konto noch nicht freigeschaltet.",
    e_notOwned: "Nur der Eigentümer der Sammlung kann ein Kabinett anlegen.",
    e_titleRequired: "Geben Sie Ihrem Kabinett einen Titel.",
    e_generic: "Speichern ist im Moment nicht möglich.",
    e_self: "Sie können sich nicht selbst einladen.",
    e_email: "Diese E-Mail-Adresse ist ungültig.",
    e_already: "Diese Version ist bereits mit dieser Person geteilt.",
    e_tooMany: "Sie haben die maximale Anzahl aktiver Freigaben erreicht.",
    e_duration: "Ungültige Freigabedauer.",
    cardEyebrow: "Neu",
    cardBody: "Stellen Sie aus einer Sammlung eine Hängung zusammen: auswählen, ordnen, erzählen und dann mit einer nahestehenden Person teilen.",
    cardCreate: (t) => `Kabinett anlegen: ${t}`,
    cardNoCollection: "Legen Sie zuerst eine Sammlung an, um darin ein Kabinett zusammenzustellen.",
    cardShared: (n) => `${n} mit mir geteilte${n > 1 ? "" : "s"} Kabinett${n > 1 ? "e" : ""}`,
    sharedTitle: "Mit mir geteilte Kabinette",
    sharedLoadError: "Ihre Einladungen konnten gerade nicht geladen werden.",
    sharedNone: "Keine aktive Einladung für die Adresse, mit der Sie angemeldet sind.",
    sharedFrom: (n) => ` · von ${n}`,
    sharedUntil: (d) => ` · bis ${d}`,
    unavailableTitle: "Kabinett nicht verfügbar",
    unavailableBody: "Dieser Zugang existiert nicht, ist abgelaufen oder wurde widerrufen — oder Sie sind nicht mit der eingeladenen E-Mail-Adresse angemeldet.",
    myInvitations: "Meine Einladungen",
    sharedReadOnly: (d) => `Mit Ihnen geteilt, schreibgeschützt · Zugang bis ${d}`,
    sidebarLink: "Kuriositätenkabinett",
  },
};

export const pickLang = (lang) => {
  const l = String(lang || "fr").slice(0, 2).toLowerCase();
  return D[l] ? l : "fr";
};

// Traduction hors composant (messages d'erreur du service) : key + éventuels arguments.
export function cabinetText(lang, key, ...args) {
  const v = D[pickLang(lang)][key] ?? D.fr[key];
  return typeof v === "function" ? v(...args) : v;
}

// Dans un composant : const { t, lang, locale } = useCabinetText();
export function useCabinetText() {
  const { i18n } = useTranslation();
  const lang = pickLang(i18n?.resolvedLanguage || i18n?.language);
  return { t: (key, ...args) => cabinetText(lang, key, ...args), lang, locale: LOCALES[lang] };
}
